import { FC, useCallback, useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import classNames from 'classnames';
import { debounce } from 'utils';

import { Typography } from '@material-ui/core';

import NibAsyncAutoComplete from 'components/common/NibAsyncAutoCompleteUi/NibAsyncAutoComplete';

import css from './filters.module.sass';

type Option = {
  id: string | number;
  name: string;
  [key: string]: any;
};

interface UniversalSelectProps {
  title?: string;
  label?: string;
  placeholder?: string;
  alias: string;
  value: string | number | null;
  onFilter: (alias: string, value: string | number | null) => void;
  fetchOptions: (searchTerm: string) => Promise<any[]>;
  minSearchLength?: number;
  className?: string;
  idKey?: string;
  nameKey?: string;
  defaultSearchValue?: string;
  disabled?: boolean;
  useFetchDispatch?: boolean;
  withDispatch?: boolean;
  inputClassName?: string;
}

const mapOptions = (data: any[], idKey: string, nameKey: string): Option[] => {
  return data?.map((item) => ({
    id: item[idKey],
    name: item[nameKey],
  }));
};

const NibSelectWithSearch: FC<UniversalSelectProps> = ({
  title = 'Choose Option',
  label = '',
  placeholder = 'Select',
  alias,
  value,
  onFilter,
  fetchOptions,
  minSearchLength = 1,
  className = '',
  idKey = 'id',
  nameKey = 'name',
  defaultSearchValue,
  disabled = false,
  useFetchDispatch = true,
  withDispatch = true,
  inputClassName = '',
}) => {
  const dispatch = useDispatch();
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchDebounced = useCallback(
    debounce(async (searchTerm: string) => {
      setLoading(true);
      if (searchTerm && searchTerm.length >= minSearchLength) {
        const data = useFetchDispatch
          ? await dispatch(fetchOptions(searchTerm || ''))
          : await fetchOptions(searchTerm || '');
        setOptions(mapOptions(data, idKey, nameKey));
      } else {
        setOptions([]);
      }
      setLoading(false);
    }, 300),
    [dispatch, fetchOptions, idKey, nameKey, minSearchLength],
  );

  const handleSearch = (searchTerm: string) => {
    fetchDebounced(searchTerm);
  };

  useEffect(() => {
    if (defaultSearchValue) {
      handleSearch(defaultSearchValue);
    }
  }, [defaultSearchValue]);

  const selectedOption = options?.find((option) => option.id === value);

  return (
    <div className={classNames(css.wrapper, className)}>
      {title && (
        <div className={css.filterTitle}>
          <Typography>{title}</Typography>
        </div>
      )}
      <NibAsyncAutoComplete
        disabled={disabled}
        // @ts-ignore
        value={selectedOption}
        label={label}
        placeholder={placeholder}
        // @ts-ignore
        options={options}
        // @ts-ignore
        onChange={(option: Option | null) =>
          withDispatch
            ? dispatch(onFilter(alias, option ? option.id : null))
            : onFilter(alias, option ? option.id : null)
        }
        handleInput={handleSearch}
        loading={loading}
        inputProps={{
          className: classNames(css.companySelect, css.filterInput, css.nibSelectWithSearchInput, inputClassName),
        }}
      />
    </div>
  );
};

export default NibSelectWithSearch;
